import {faBatteryFull} from "@fortawesome/pro-solid-svg-icons/faBatteryFull";
import {faCamera} from "@fortawesome/pro-solid-svg-icons/faCamera";
import {faLaptop} from "@fortawesome/pro-solid-svg-icons/faLaptop";
import {faMobileAlt} from "@fortawesome/pro-solid-svg-icons/faMobileAlt";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {BatteryDetailsType} from "@twigsby/client-data";
import {FlexAlign, FlexGap, FlexJustify, StackHorizontal} from "@twigsby/frontend-framework";
import {FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import React, {useState} from "react";
import {useForwardingBatchProvider} from "../../../Providers/ForwardingBatchProvider";
import {BatteryTypeEntryType, RenderBatchSectionType} from "../../../Types/ForwardingBatchTypes";
import {AccountNamesCombobox} from "../Controls/AccountNamesCombobox";

type BulletPointEntryType = {
    value: string;
    important?: boolean;
}

type BatteryEntryType = {
    name: string;
    title: string;
    media: any;
    description: Array<BulletPointEntryType>;
    defaultSelected: boolean;
    callBack: (state: boolean) => void;
    complete: boolean;
    readOnly: boolean;
}

const BatchBatteryDeclaration = (props: RenderBatchSectionType) => {
    const {currentBatchData, UpdateBatteryDeclaration } = useForwardingBatchProvider();

    const formMethods = useForm({
        defaultValues: {
            batteryDeclaration:  currentBatchData?.batteryDetails,
        },
        mode: "onChange"
    });

    const [ batteryDeclaration, setBatteryDeclaration] = useState<BatteryDetailsType>({
        lithiumBatteriesInEquipment: currentBatchData?.batteryDetails?.lithiumBatteriesInEquipment || false,
        lithiumBatteriesWithEquipment: currentBatchData?.batteryDetails?.lithiumBatteriesWithEquipment || false,
        metalBatteriesWithEquipment: currentBatchData?.batteryDetails?.metalBatteriesWithEquipment || false,
        shippersName: currentBatchData?.batteryDetails?.shippersName || ''
    });

    const UpdateSelectedBatteries = (name: string, value: any) => {
        const newData = {
            ...batteryDeclaration,
            [name]: value,
        };

        setBatteryDeclaration(newData);
        UpdateBatteryDeclaration(newData);
    }

    if(props.complete) return <BatchBatteryDeclarationComplete {...props} />

    return (
        <FormProvider {...formMethods}>
            <div>
                <h5 className={'mb-xs'}>{`Step ${props.sectionIndex}: ${props.section.name}`}</h5>

                <div className={'row mb-md'}>
                    <div className={'col-md-4'}>
                        <div className={'input-group'}>
                            <label>Shipper's Name ( {batteryDeclaration.shippersName} )</label>
                            <AccountNamesCombobox
                                name={'shippersName'}
                                defaultValue={currentBatchData?.batteryDetails?.shippersName}
                                callback={(item) => UpdateSelectedBatteries('shippersName', item.label)}
                                disabled={props.readOnly || props.complete}
                            />
                        </div>
                    </div>
                </div>

                <div className={'row'}>
                    <div className={'col-md-8'}>
                        <div className={'secondary p-sm round mb-xs'}>
                            Lithium
                        </div>
                        <div className={'row'}>
                            <div className={'col-md-6 align-'}>
                                <LithiumBatteryWithEquipment
                                    onChangeCallback={(state) => UpdateSelectedBatteries('lithiumBatteriesWithEquipment', state)}
                                    complete={props.complete}
                                    readOnly={props.readOnly}
                                    defaultSelected={batteryDeclaration.lithiumBatteriesWithEquipment}
                                />
                            </div>
                            <div className={'col-md-6'}>
                                <LithiumBatteryInEquipment
                                    onChangeCallback={(state) => UpdateSelectedBatteries('lithiumBatteriesInEquipment', state)}
                                    complete={props.complete}
                                    readOnly={props.readOnly}
                                    defaultSelected={batteryDeclaration.lithiumBatteriesInEquipment}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={'col-md-4'}>
                        <div className={'secondary p-sm round mb-xs'}>
                            Metal
                        </div>
                        <div className={'row'}>
                            <div className={'col-md-12'}>
                                <MetalBatteryWithEquipment
                                    onChangeCallback={(state) => UpdateSelectedBatteries('metalBatteriesWithEquipment', state)}
                                    complete={props.complete}
                                    readOnly={props.readOnly}
                                    defaultSelected={batteryDeclaration.metalBatteriesWithEquipment}
                                />
                            </div>
                        </div>
                    </div>
                </div>


                {!props.complete && (
                    <div className={'row'}>
                        <div className={'col-md-4'}>
                            <p>Maximum of 20 watt hours per cell, or 100 watt hours per battery</p>
                            <p>Typically used to power portable, and larger, electronic devices (Laptops,
                                Cordless Power
                                Tools, etc).</p>
                        </div>
                        <div className={'col-md-4'}>
                        </div>
                        <div className={'col-md-4'}>
                            <p>Maximum of 20 watt hours per cell, or 100 watt hours per battery</p>
                            <p>Commonly single use, and easily interchangeable, used in household items like
                                remote
                                controls and chldrens toys (AA, AAA, D, etc).</p>
                        </div>
                    </div>
                )}
            </div>
        </FormProvider>
    )
}

const BatchBatteryDeclarationComplete = (props: RenderBatchSectionType) => {
    const {currentBatchData} = useForwardingBatchProvider();

    return  (
        <div>
            <h5 className={'mb-xs'}>{props.section.name}</h5>

            {currentBatchData?.batteryDetails?.lithiumBatteriesWithEquipment && (
                <LithiumBatteryWithEquipment
                    onChangeCallback={() => null}
                    complete={props.complete}
                    readOnly={props.readOnly}
                    defaultSelected={false}
                />
            )}

            {currentBatchData?.batteryDetails?.lithiumBatteriesInEquipment && (
                <LithiumBatteryInEquipment
                    onChangeCallback={() => null}
                    complete={props.complete}
                    readOnly={props.readOnly}
                    defaultSelected={false}
                />
            )}

            {currentBatchData?.batteryDetails?.metalBatteriesWithEquipment && (
                <MetalBatteryWithEquipment
                    onChangeCallback={() => null}
                    complete={props.complete}
                    readOnly={props.readOnly}
                    defaultSelected={false}
                />
            )}
        </div>
    )
}

const BatteryEntry = (props: BatteryEntryType) => {
    const [ selected, setSelected ] = useState(props.defaultSelected);

    const HandleCallback = () => {
        setSelected(!selected);
        props.callBack(!selected);
    }

    return (
        <div onClick={() => !props.readOnly && !props.complete ? HandleCallback() : null} className={`upload-block ${selected ? 'primary' : ''} ${(props.complete || props.readOnly) ? 'disabled' : ''} align-stretch`}>
            <div className={`card-body`}>
                {props.media}
                <h5>{props.title}</h5>
                <ul>
                    {props.description.map((entry, index) =>
                        <li key={`description${props.name}${index}`} className={entry.important ? 'list-alert' : ''}>{entry.value}</li>
                    )}
                </ul>
            </div>
        </div>
    )
}

const LithiumBatteryInEquipment = (props: BatteryTypeEntryType) => {
    return (
        <BatteryEntry
            name={'lithiumBatteriesInEquipment'}
            media={
                <StackHorizontal
                    gap={FlexGap.ExtraSmall}
                    align={FlexAlign.Center}
                    justify={FlexJustify.Center}
                    extraClass={'mb-lg'}
                >
                    <FontAwesomeIcon icon={faMobileAlt} size={"3x"}/>
                    <FontAwesomeIcon icon={faCamera} size={"3x"}/>
                </StackHorizontal>
            }
            title={'Installed in Equipment'}
            description={[
                {value: 'No Powerbanks', important: true},
            ]}
            defaultSelected={props.defaultSelected}
            callBack={(state) => props.onChangeCallback(state)}
            complete={props.complete}
            readOnly={props.readOnly}
        />
    )
}

const LithiumBatteryWithEquipment = (props: BatteryTypeEntryType) => {
    return (
        <BatteryEntry
            name={'lithiumBatteriesWithEquipment'}
            media={
                <StackHorizontal
                    gap={FlexGap.ExtraSmall}
                    align={FlexAlign.Center}
                    justify={FlexJustify.Center}
                    extraClass={'mb-lg'}
                >
                    <FontAwesomeIcon icon={faBatteryFull} size={"3x"} rotation={270}/>
                    <FontAwesomeIcon icon={faBatteryFull} size={"3x"} rotation={270}/>
                    <FontAwesomeIcon icon={faLaptop} size={"3x"}/>
                </StackHorizontal>
            }
            title={'Packed With Equipment'}
            description={[
                {value: 'Batteries must be for the device that they are shipped with'},
                {value: 'No Powerbanks', important: true},
            ]}
            defaultSelected={props.defaultSelected}
            callBack={(state) => props.onChangeCallback(state)}
            complete={props.complete}
            readOnly={props.readOnly}
        />
    )
}

const MetalBatteryWithEquipment = (props: BatteryTypeEntryType) => {
    return (
        <BatteryEntry
            name={'metalBatteriesWithEquipment'}
            media={
                <StackHorizontal
                    gap={FlexGap.ExtraSmall}
                    align={FlexAlign.Center}
                    justify={FlexJustify.Center}
                    extraClass={'mb-lg'}
                >
                    <FontAwesomeIcon icon={faMobileAlt} size={"3x"}/>
                    <FontAwesomeIcon icon={faCamera} size={"3x"}/>
                </StackHorizontal>
            }
            title={'Packed With Equipment'}
            description={[
                {value: 'Batteries must not be inside device/installed in equipment'},
                {value: 'Batteries must be in original manufacturer packaging'},
                {value: 'Batteries must be for the device that they are shipped with'},
                {value: 'Max 2 Packs per shipment', important: true},
            ]}
            defaultSelected={props.defaultSelected}
            callBack={(state) => props.onChangeCallback(state)}
            complete={props.complete}
            readOnly={props.readOnly}
        />
    )
}

export {
    BatchBatteryDeclaration
}
